<template>
  <Modal
    v-if="loanProgram"
    data-cy="modal"
    title="Adjust the Minimum Financed Amount"
    class-name="adjustMinimumFinanceAmountModal"
    :disabled="!amountChanged"
    @submit="handleSave"
    @hide-modal="emit('hide-modal')"
  >
    <div class="section-spacing-10 mb-3">
      <div data-cy="programName">
        <strong>Program Name: </strong>{{ loanProgram.description }}
      </div>
      <div class="d-flex align-items-center">
        <div>
          <strong>Lender Minimum Amount: </strong>
          <span data-cy="lenderMinAmount">{{
            dollars(loanProgram.min_amount_cents)
          }}</span>
        </div>
        <div class="ml-auto">
          <div class="d-flex align-items-center">
            <div class="mr-1">
              <strong>Preferred Minimum Amount: </strong>
            </div>
            <div style="width: 150px">
              <TextField
                v-model="formData.preferredMinAmountCents"
                :clearable="formData.preferredMinAmountCents !== 0"
                data-cy="preferredMinAmountCentsInput"
                currency
                class="full-width"
                :class="{ 'is-invalid': errorMessages.preferredMinAmountCents }"
                type="text"
                :error="!!errorMessages.preferredMinAmountCents"
              />
            </div>
          </div>
          <div
            v-if="errorMessages.preferredMinAmountCents"
            class="text-danger error-text text-right"
            data-cy="errorPreferredMinAmountCents"
          >
            {{ errorMessages.preferredMinAmountCents }}
          </div>
        </div>
      </div>
    </div>
    <div v-if="loanProgram.min_amount_cents" class="note">
      <strong>Note: </strong>The preferred minimum amount cannot be lower than
      the lender's minimum amount of
      <span data-cy="lenderMinAmountNote">
        {{ dollars(loanProgram.min_amount_cents) }}.
      </span>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";

import Modal from "@/components/Modal.vue";
import TextField from "@/components/TextField.vue";
import { dollars } from "@/mixins";
import { LoanProgram } from "../types";

const props = defineProps({
  id: {
    type: [String, Number],
    default: () => undefined
  }
});
const emit = defineEmits(["hide-modal", "save-success"]);

const store = useStore();
const loanProgram = ref<LoanProgram | null>(null);
const errorMessages = ref({
  preferredMinAmountCents: ""
});
const formData = ref({
  preferredMinAmountCents: 0
});

const amountChanged = computed(() => {
  const existingValue = loanProgram.value?.preferred_min_amount_cents || 0;
  const currentValue = formData.value?.preferredMinAmountCents || 0;
  return currentValue !== existingValue;
});

const lenderMinAmountCents = computed(() => {
  if (!loanProgram.value) return 0;
  return loanProgram.value.min_amount_cents || 0;
});

const lenderMaxAmountCents = computed(() => {
  if (!loanProgram.value) return 0;
  return loanProgram.value.max_amount_cents || 0;
});

onMounted(() => {
  clearErrorMessages();
  refresh();
});

function clearErrorMessages() {
  Object.keys(errorMessages.value).forEach((key) => {
    errorMessages.value[key] = "";
  });
}

async function refresh() {
  loanProgram.value = await store.dispatch("fetchLoanProgram", props.id);
  formData.value.preferredMinAmountCents =
    loanProgram.value!.preferred_min_amount_cents || 0;
}

async function handleSave() {
  clearErrorMessages();
  const minAmountCents = formData.value.preferredMinAmountCents;
  if (minAmountCents !== 0) {
    if (minAmountCents < lenderMinAmountCents.value) {
      errorMessages.value.preferredMinAmountCents = `Amount must be at least ${dollars(
        lenderMinAmountCents.value
      )}`;
    } else if (minAmountCents >= lenderMaxAmountCents.value) {
      errorMessages.value.preferredMinAmountCents = `Amount must be less than ${dollars(
        lenderMaxAmountCents.value
      )}`;
    }
  }
  if (!Object.values(errorMessages.value).every((v) => v === "")) return;
  await store.dispatch("updateLoanProgram", {
    id: loanProgram.value!.id,
    preferred_min_amount_cents: minAmountCents === 0 ? null : minAmountCents
  });
  window.notify.success("Loan program updated.");
  emit("save-success");
  emit("hide-modal");
}
</script>

<style lang="scss">
.adjustMinimumFinanceAmountModal {
  // TODO: Make the note a shared component or determine if we have existing styling
  .note {
    background: var(--light-gray);
    border: 1px solid #6d767d;
    border-radius: 4px;
    padding: 8px 16px;
  }
  .error {
    border: 1px solid var(--danger);
  }
}
</style>
